import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { auth } from "../firebase";
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

export default function AdminLogoEditor() {
    const [logo, setLogo] = useState({
        text: "",
        image: "",
        useImage: false
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    
    // Cropping states
    const [cropImageSrc, setCropImageSrc] = useState(null);
    const [crop, setCrop] = useState({ unit: '%', width: 50, aspect: 1 });
    const [completedCrop, setCompletedCrop] = useState(null);
    const imgRef = useRef(null);

    useEffect(() => {
        const fetchLogo = async () => {
            try {
                const token = await auth.currentUser.getIdToken();
                const res = await axios.get("/api/public/logo", {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                setLogo(res.data.content || { text: "", image: "", useImage: false });
            } catch (error) {
                console.error("Error fetching logo:", error);
                toast.error("Error fetching logo settings");
            } finally {
                setLoading(false);
            }
        };
        
        fetchLogo();
    }, []);

    const handleChange = (field, value) => {
        setLogo({ ...logo, [field]: value });
    };

    const saveLogo = async () => {
        if (!logo.text.trim()) {
            toast.error("Logo text is required");
            return;
        }

        setSaving(true);
        try {
            const token = await auth.currentUser.getIdToken();
            await axios.put("/api/public/logo", { content: logo }, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            toast.success("Logo settings saved!");
        } catch {
            toast.error("Failed to save logo settings");
        } finally {
            setSaving(false);
        }
    };

    const handleFileSelect = (file) => {
        if (!file) return;

        // Validate file type
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/heic'];
        if (!allowedTypes.includes(file.type)) {
            toast.error('Only JPG, PNG, and HEIC files are allowed for cropping');
            return;
        }

        // Validate file size (10MB)
        if (file.size > 10 * 1024 * 1024) {
            toast.error('File size must be less than 10MB');
            return;
        }

        // Read the file and show crop modal
        const reader = new FileReader();
        reader.addEventListener('load', () => {
            setCropImageSrc(reader.result);
        });
        reader.readAsDataURL(file);
    };

    const getCroppedImg = async () => {
        if (!completedCrop || !imgRef.current) {
            toast.error('Please select a crop area');
            return;
        }

        const image = imgRef.current;
        const canvas = document.createElement('canvas');
        const scaleX = image.naturalWidth / image.width;
        const scaleY = image.naturalHeight / image.height;
        
        // Set canvas to exactly 250x250
        canvas.width = 250;
        canvas.height = 250;
        const ctx = canvas.getContext('2d');

        // Clear canvas to transparent (instead of default black)
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        ctx.drawImage(
            image,
            completedCrop.x * scaleX,
            completedCrop.y * scaleY,
            completedCrop.width * scaleX,
            completedCrop.height * scaleY,
            0,
            0,
            250,
            250
        );

        return new Promise((resolve) => {
            canvas.toBlob((blob) => {
                resolve(blob);
            }, 'image/png'); // Use PNG to preserve transparency
        });
    };

    const handleCropComplete = async () => {
        setUploading(true);
        try {
            const croppedBlob = await getCroppedImg();
            if (!croppedBlob) {
                setUploading(false);
                return;
            }

            const token = await auth.currentUser.getIdToken();
            const formData = new FormData();
            formData.append('file', croppedBlob, 'logo.png');
            formData.append('subfolder', 'logo');

            const response = await axios.post('/api/upload-image', formData, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data',
                },
            });

            if (response.data.success) {
                handleChange('image', response.data.url);
                toast.success('Logo image uploaded successfully!');
                setCropImageSrc(null);
                setCompletedCrop(null);
            }
        } catch (error) {
            console.error('Error uploading image:', error);
            toast.error(error.response?.data?.detail || 'Failed to upload image');
        } finally {
            setUploading(false);
        }
    };

    const handleCancelCrop = () => {
        setCropImageSrc(null);
        setCompletedCrop(null);
    };

    if (loading) return <p>Loading logo settings...</p>;

    return (
        <>
            {/* Crop Modal */}
            {cropImageSrc && (
                <div 
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.8)',
                        zIndex: 9999,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '20px'
                    }}
                >
                    <div 
                        style={{
                            backgroundColor: 'white',
                            borderRadius: '8px',
                            padding: '20px',
                            maxWidth: '90vw',
                            width: '100%',
                            maxHeight: '90vh',
                            display: 'flex',
                            flexDirection: 'column'
                        }}
                    >
                        <h5 className="mb-3">Crop Logo Image (will be resized to 250x250 pixels)</h5>
                        <div style={{ 
                            flex: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden',
                            minHeight: 0
                        }}>
                            <ReactCrop
                                crop={crop}
                                onChange={c => setCrop(c)}
                                onComplete={c => setCompletedCrop(c)}
                                aspect={1}
                            >
                                <img 
                                    ref={imgRef}
                                    src={cropImageSrc} 
                                    alt="Crop preview"
                                    style={{ 
                                        maxWidth: '100%',
                                        maxHeight: '70vh',
                                        objectFit: 'contain'
                                    }}
                                />
                            </ReactCrop>
                        </div>
                        <div className="d-flex gap-2 mt-3">
                            <button 
                                className="btn btn-admin"
                                onClick={handleCropComplete}
                                disabled={uploading}
                            >
                                {uploading ? 'Uploading...' : 'Upload Cropped Image'}
                            </button>
                            <button 
                                className="btn btn-secondary"
                                onClick={handleCancelCrop}
                                disabled={uploading}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="mb-3">
                <label className="form-label fw-bold">Logo Text <span className="text-danger">*</span></label>
                <input 
                    className="form-control" 
                    value={logo.text} 
                    onChange={(e) => handleChange("text", e.target.value)}
                    placeholder="e.g., NSC"
                    required
                />
                <small className="text-body-secondary">
                    This text will be used as the default logo and alt text for the logo image
                </small>
            </div>

            <div className="mb-3">
                <label className="form-label fw-bold">Logo Image <small className="text-body-secondary">(Optional)</small></label>
                <input 
                    className="form-control mb-2" 
                    value={logo.image} 
                    onChange={(e) => handleChange("image", e.target.value)}
                    placeholder="Image URL"
                />
                <div className="input-group mb-2">
                    <input 
                        type="file" 
                        className="form-control" 
                        accept=".jpg,.jpeg,.png,.heic"
                        onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) handleFileSelect(file);
                            e.target.value = null; // Reset file input
                        }}
                        disabled={uploading}
                    />
                    <button 
                        className="btn btn-outline-secondary" 
                        type="button"
                        disabled
                    >
                        {uploading ? 'Uploading...' : 'Select & Crop'}
                    </button>
                </div>
                <small className="text-body-secondary">
                    Image will be cropped to exactly 250x250 pixels
                </small>
                {logo.image && (
                    <div className="mt-3">
                        <p className="mb-2 fw-bold">Preview:</p>
                        <img 
                            src={logo.image} 
                            alt={logo.text || "Logo preview"} 
                            style={{ maxWidth: '300px', maxHeight: '150px', objectFit: 'contain' }}
                            className="img-thumbnail"
                        />
                    </div>
                )}
            </div>

            <div className="mb-4">
                <div className="form-check">
                    <input 
                        className="form-check-input" 
                        type="checkbox" 
                        id="useImageCheckbox"
                        checked={logo.useImage}
                        onChange={(e) => handleChange("useImage", e.target.checked)}
                        disabled={!logo.image}
                    />
                    <label className="form-check-label" htmlFor="useImageCheckbox">
                        Use logo image instead of text
                    </label>
                    {!logo.image && (
                        <div>
                            <small className="text-body-secondary">
                                Upload an image first to enable this option
                            </small>
                        </div>
                    )}
                </div>
            </div>

            <button className="btn btn-admin" onClick={saveLogo} disabled={saving}>
                {saving ? "Saving..." : "Save Logo Settings"}
            </button>
        </>
    );
}